import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type Project } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';

const PROJECTS = 'projects';

const projectConverter = buildConverter<Project>(
  (data) =>
    ({
      id: data.id,
      name: data.name,
      description: data.description,
      status: data.status,
      ownerId: data.ownerId,
      environment: data.environment,
      isDemo: data.isDemo,
      standards: data.standards ?? [],
      vehicleIds: data.vehicleIds ?? [],
      defaultRouteId: data.defaultRouteId ?? undefined,
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
      updatedAt: toTimestampString(data.updatedAt) ?? new Date().toISOString(),
    }) as Project,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreProjectRepository {
  async findById(id: string): Promise<Project | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, PROJECTS, id).withConverter(projectConverter));
    return snap.data();
  }

  async findAll(): Promise<Project[]> {
    return this.findByEnvironment();
  }

  async findByEnvironment(environment: string = getCurrentEnvironment()): Promise<Project[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(collection(db, PROJECTS), where('environment', '==', environment)).withConverter(projectConverter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findAccessibleProjectIds(_uid: string): Promise<string[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const projects = await this.findByEnvironment();
    return projects.map((p) => p.id);
  }

  async findByDemo(environment: string = getCurrentEnvironment()): Promise<Project[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, PROJECTS),
      where('environment', '==', environment),
      where('isDemo', '==', true)
    ).withConverter(projectConverter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(project: Project): Promise<Project> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const existing = await this.findById(project.id);
    const data = { ...project, environment: getCurrentEnvironment() };
    if (existing) {
      await updateDoc(doc(db, PROJECTS, project.id), { ...data, updatedAt: serverTimestamp() });
    } else {
      await setDoc(doc(db, PROJECTS, project.id).withConverter(projectConverter), data);
    }
    return { ...data, createdAt: existing?.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, PROJECTS, id));
  }
}

export const createFirestoreProjectRepository = (): FirestoreProjectRepository => new FirestoreProjectRepository();
